import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { CanvasNote, CommentDraft, Goal, Habit, HabitLog, LifeSpace, Project, ProjectComment, ReminderDraft, RewardUsage, Section, Subtask, SubtaskDraft, Tag, Task, TaskCascade, TaskComment, TaskPlacement, TaskReminder, TimeEntry } from '@/src/renderer/lib/db/models'
import { isStandardReminder } from '@/src/renderer/lib/db/models'
import { STANDARD_REMINDER, gainsStartTime, reminderFromDraft } from '@/src/renderer/lib/reminders/reminderFromDraft'
import { planIndent, planNest, planOutdent, planUnnest, sameList } from '@/src/renderer/lib/db/taskNesting'
import { ancestorsOf, childMap, depthOf, descendantsOf, MAX_SUBTASK_DEPTH, rootOf, treeEntriesOf } from '@/src/renderer/lib/db/taskTree'
import { ancestorsCompletedBy } from '@/src/renderer/lib/db/branchCompletion'
import { createTaskCounter } from '@/src/renderer/lib/db/taskCounters'
import { MIN_FOCUS_SECONDS } from '@/src/renderer/lib/db/models'
import { isClosedByArchive, isCompletedTask, isDormantTask, isPlannedWithinNextWeek, isInDateRange, isProjectTask, isQuickNote, isRecurringTask, chainKeepsChildListed, withoutSucceededRounds } from '@/src/renderer/lib/db/taskFilters'
import { isFavoriteTask } from '@/src/renderer/lib/favorites'
import type { Avatar, AvatarCategory } from '@/src/renderer/lib/db/types'
import type { UserDisplayProfile } from '@/src/renderer/lib/profile/displayProfile'
import type { DataSource } from './dataSource'
import { nextTagColor } from './tagColors'
import { isIntroContent } from '@/src/renderer/lib/import/importedFrom'
import { useStableSidebarCounts } from './sidebarCounts'
import { useMidnightRefresh } from '@/src/renderer/lib/date/useMidnightRefresh'
import { hasUniqueOrder, orderBetween } from '@/src/renderer/lib/db/subtaskOrder'
import { descendantsLosingRecurrence, reportChildRecurrenceDropped, type ChildRecurrenceDropped } from '@/src/renderer/lib/recurrence/childRecurrenceDropped'
import { titleConsistencyChanges } from '@/src/renderer/lib/projectIcon/projectTitle'
import { applyProjectMove, canHoldSubproject, flattenProjectTree, MAX_PROJECT_DEPTH, projectDepth, projectDescendantIds, projectSubtreeHeight, type ProjectTreeChange } from '@/src/renderer/lib/projectTree'
import { addDays } from '@/src/renderer/lib/date/calendar'
import { isOpenTask, latestCanvasNoteList, latestFocusedTaskList, latestTaskList, searchCanvasNoteList, searchTaskList } from '@/src/renderer/lib/search/matchSearch'

/** Die lebenden Kinder einer Aufgabe, in Checklisten-Reihenfolge (T14 #179: Unteraufgaben sind Aufgaben). */
function orderedChildren(all: Task[], parentId: string): Task[] {
  return all
    .filter(t => t.parentTaskId === parentId && !t.deletedAt)
    .sort((a, b) => a.order - b.order)
}

/**
 * Dieselben Zeilen, aber mit eindeutiger Reihenfolge — sonst rechnet man auf
 * Sand (siehe `hasUniqueOrder`). Meldet die Liste und die Gesamtheit, in der
 * die neuen Werte schon stehen.
 */
function orderedForWriting(all: Task[], parentId: string): { ordered: Task[]; all: Task[] } {
  const ordered = orderedChildren(all, parentId)
  if (hasUniqueOrder(ordered.map(s => s.order))) return { ordered, all }
  return { ordered: ordered.map((s, i) => ({ ...s, order: i })), all: renumberIn(all, ordered) }
}

/** Vergibt 0, 1, 2 … neu — der Notausgang, wenn zwischen zwei Zeilen kein Platz mehr ist. */
function renumberIn(all: Task[], ordered: Task[]): Task[] {
  const fresh = new Map(ordered.map((s, i) => [s.id, i]))
  return all.map(s => (fresh.has(s.id) ? { ...s, order: fresh.get(s.id)! } : s))
}

/** Eine Kind-Aufgabe in der Form der Checkliste — wie `db.asSubtask`. */
function asSubtask(task: Task): Subtask {
  return {
    id: task.id,
    taskId: task.parentTaskId ?? '',
    title: task.title,
    done: task.status === 'completed',
    sortOrder: task.order,
    userId: task.userId,
    cloudId: task.cloudId,
    deletedAt: task.deletedAt,
    importedFrom: task.importedFrom ?? null,
    attention: task.attention,
    startDate: task.startDate,
    startTime: task.startTime,
    startPeriod: task.startPeriod,
    dueDate: task.dueDate,
    dueTime: task.dueTime,
    duePeriod: task.duePeriod,
    estimatedDuration: task.estimatedDuration,
    durationMode: task.durationMode,
    tags: task.tags,
    pausedAt: task.pausedAt,
    rewardId: task.rewardId,
    recurrence: task.recurrence,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    lastModified: task.lastModified,
    synced: task.synced,
    syncedAt: task.syncedAt,
  }
}

/** Eine Unteraufgabe als Aufgabe: Ort und Lebensbereich vom Elternteil — wie `db.addChild`. */
function childTask(
  parent: Task | undefined,
  fields: SubtaskDraft & { id: string; parentId: string; order: number },
): Task {
  const estimatedDuration = fields.estimatedDuration ?? null
  return makeTask({
    id: fields.id,
    title: fields.title,
    parentTaskId: fields.parentId,
    status: fields.done ? 'completed' : 'todo',
    completedAt: fields.done ? (fields.completedAt ?? new Date()) : null,
    order: fields.order,
    description: fields.description ?? null,
    attention: fields.attention ?? null,
    startDate: fields.startDate ?? null,
    startTime: fields.startTime ?? null,
    dueDate: fields.dueDate ?? null,
    estimatedDuration,
    durationMode: estimatedDuration !== null ? (fields.durationMode ?? 'duration') : null,
    tags: fields.tags ?? [],
    recurrence: fields.recurrence ?? null,
    lifeSpace: parent?.lifeSpace ?? 'personal',
    projectId: parent?.projectId ?? null,
    sectionId: parent?.sectionId ?? null,
    importedFrom: fields.importedFrom ?? null,
    completedCount: fields.completedCount ?? null,
    postponedCount: fields.postponedCount ?? null,
  })
}

/** Geschwister ohne abgelöste Runden; der Nachfolger darf überall unter `rootId` stehen (#181). */
function keepCurrentRounds(tasks: Task[], rootId: string, children?: ReadonlyMap<string, Task[]>) {
  const family = descendantsOf(tasks, rootId, undefined, children)
  return (siblings: readonly Task[]) => withoutSucceededRounds(siblings, family)
}

/** Zieht alle Ebenen darunter mit (#181), wenn eine Zeile Projekt, Abschnitt oder Lebensbereich wechselt — wie `db.moveChildrenWithParent`. */
function withChildrenFollowing(all: Task[], changed: Map<string, Partial<Task>>): Task[] {
  const follow = new Map<string, Partial<Task>>()
  for (const [id, changes] of changed) {
    const location: Partial<Task> = {}
    if (changes.projectId !== undefined) location.projectId = changes.projectId
    if (changes.sectionId !== undefined) location.sectionId = changes.sectionId
    if (changes.lifeSpace !== undefined) location.lifeSpace = changes.lifeSpace
    if (Object.keys(location).length === 0) continue
    for (const row of descendantsOf(all, id)) follow.set(row.id, { ...follow.get(row.id), ...location })
  }
  return follow.size === 0 ? all : all.map(t => (follow.has(t.id) ? { ...t, ...follow.get(t.id) } : t))
}

/**
 * Wie `db.dropChildRecurrence` (#205, Punkt 5): Bekommt eine Zeile eine Wiederholung, verliert alles
 * darunter seine eigene (#181). `ids`: die Zeilen, die gerade eine Wiederholung erhielten.
 */
function dropChildRecurrence(all: Task[], before: Task[], ids: readonly string[]): { tasks: Task[]; dropped: ChildRecurrenceDropped[] } {
  const dropped: ChildRecurrenceDropped[] = []
  let tasks = all
  for (const id of ids) {
    const parent = tasks.find(t => t.id === id)
    const losing = descendantsLosingRecurrence(parent, { recurrence: parent?.recurrence ?? null }, tasks)
    if (losing.length === 0) continue
    const losingIds = new Set(losing.map(child => child.id))
    tasks = tasks.map(t => (losingIds.has(t.id) ? { ...t, recurrence: null } : t))
    dropped.push({
      parentId: id,
      parentBefore: before.find(t => t.id === id)?.recurrence ?? null,
      children: losing.map(child => ({ id: child.id, recurrence: child.recurrence! })),
    })
  }
  return { tasks, dropped }
}

/** What an in-memory source starts with. */
export interface MemoryData {
  tags?: Tag[]
  /** Checklisten-Zeilen; die Quelle legt sie als Kind-Aufgaben an (T14 #179). */
  subtasks?: Subtask[]
  rewardUsages?: RewardUsage[]
  projects?: Project[]
  canvasNotes?: CanvasNote[]
  /** The signed-in user; without it there is no name (memo author stays hidden). */
  profile?: { fullName: string | null; avatarUrl: string | null; avatarPresetId?: string | null } | null
  /** Avatar presets; images as embedded URLs (`file_path` or `cached_image_blob`), no CDN. */
  avatarCatalog?: { categories: AvatarCategory[]; avatars: Avatar[] }
  /** Tasks for what components and pages read themselves (sidebar counts, list pages). */
  tasks?: Task[]
  sections?: Section[]
  /** User settings by key. */
  settings?: Record<string, unknown>
  goals?: Goal[]
  habits?: Habit[]
  habitLogs?: HabitLog[]
  /** **Fokuszeit**: Abschnitte je Aufgabe; einer mit `endTime: null` läuft gerade. */
  timeEntries?: TimeEntry[]
  /** **Kommentare** an Aufgaben und Unteraufgaben (#182). */
  taskComments?: TaskComment[]
  /** **Kommentare** an Projekten (#184). */
  projectComments?: ProjectComment[]
  /** **Erinnerungen** an Aufgaben und Unteraufgaben. */
  taskReminders?: TaskReminder[]
  /** Synced UI preferences by key (`usePreference`). */
  preferences?: Record<string, unknown>
}

/** Der ganze Stand der Speicher-Quelle — was `onChange` vorher und nachher sieht. */
export type MemorySnapshot = Required<Omit<MemoryData, 'subtasks'>>
type MemoryState = MemorySnapshot

export interface MemoryOptions {
  /**
   * Hört jede Änderung, bevor sie gilt. Die Mobile-App schreibt hier synchron in SQLite (ADR 0036);
   * wirft es, bleibt der alte Stand — Anzeige und Ablage laufen nicht auseinander.
   */
  onChange?: (next: MemorySnapshot, prev: MemorySnapshot) => void
}

/** Stored fields every entity carries; filled so seeds only name what matters. */
function entityFields(id: string) {
  const at = new Date(0)
  return { id, createdAt: at, updatedAt: at, lastModified: at, synced: false, syncedAt: null }
}

/** A complete `Tag` for seeds; only `id` and `name` are required. */
export function makeTag(tag: Pick<Tag, 'id' | 'name'> & Partial<Tag>): Tag {
  return {
    ...entityFields(tag.id),
    color: nextTagColor(0),
    description: null,
    usageCount: 0,
    userId: null,
    cloudId: null,
    deletedAt: null,
    importedFrom: null,
    ...tag,
  }
}

/** A complete `Subtask` for seeds. */
export function makeSubtask(subtask: Pick<Subtask, 'id' | 'taskId' | 'title'> & Partial<Subtask>): Subtask {
  return {
    ...entityFields(subtask.id),
    done: false,
    sortOrder: 0,
    userId: null,
    cloudId: null,
    deletedAt: null,
    importedFrom: null,
    ...subtask,
  }
}

/** A complete `RewardUsage` for seeds; the id is the reward id, as in Dexie. */
export function makeRewardUsage(usage: Pick<RewardUsage, 'rewardId'> & Partial<RewardUsage>): RewardUsage {
  return {
    ...entityFields(usage.rewardId),
    usedAt: new Date(0),
    userId: null,
    cloudId: null,
    deletedAt: null,
    ...usage,
  }
}

/** A complete `Project` for seeds. */
export function makeProject(project: Pick<Project, 'id' | 'name' | 'lifeSpace'> & Partial<Project>): Project {
  return {
    ...entityFields(project.id),
    description: null,
    color: nextTagColor(0),
    status: 'active',
    userId: null,
    cloudId: null,
    startDate: null,
    endDate: null,
    progress: 0,
    order: 0,
    deletedAt: null,
    importedFrom: null,
    parentId: null,
    ...project,
  }
}

/** A complete open `Task` for seeds (and stories that pass tasks as props); no dates, no project. */
export function makeTask(task: Pick<Task, 'id' | 'title'> & Partial<Task>): Task {
  return {
    ...entityFields(task.id),
    description: null,
    status: 'todo',
    attention: null,
    category: 'other',
    lifeSpace: 'personal',
    projectId: null,
    sectionId: null,
    userId: null,
    cloudId: null,
    dueDate: null,
    dueTime: null,
    duePeriod: null,
    startDate: null,
    startTime: null,
    startPeriod: null,
    completedAt: null,
    estimatedDuration: null,
    actualDuration: null,
    durationMode: null,
    tags: [],
    parentTaskId: null,
    rewardId: null,
    recurrence: null,
    order: 0,
    dayOrder: null,
    deletedAt: null,
    pausedAt: null,
    importedFrom: null,
    ...task,
  }
}

/** A complete `Section` for seeds. */
export function makeSection(section: Pick<Section, 'id' | 'projectId' | 'title'> & Partial<Section>): Section {
  return {
    ...entityFields(section.id),
    userId: null,
    cloudId: null,
    order: 0,
    collapsed: false,
    deletedAt: null,
    importedFrom: null,
    description: null,
    archivedAt: null,
    ...section,
  }
}

/** A complete `CanvasNote` for seeds; sizes default to 0 like `db.createCanvasNote`. */
export function makeCanvasNote(note: Pick<CanvasNote, 'id' | 'projectId' | 'kind' | 'x' | 'y'> & Partial<CanvasNote>): CanvasNote {
  return {
    ...entityFields(note.id),
    title: null,
    content: '',
    width: 0,
    height: 0,
    rotation: 0,
    color: null,
    icon: null,
    showAuthor: false,
    font: null,
    fontSize: null,
    textAlign: null,
    zOrder: null,
    areaId: null,
    tape: null,
    taskId: null,
    convertedAt: null,
    convertedContent: null,
    userId: null,
    cloudId: null,
    deletedAt: null,
    ...note,
  }
}

/** A complete `Goal` for seeds. */
export function makeGoal(goal: Pick<Goal, 'id' | 'title' | 'lifeSpace'> & Partial<Goal>): Goal {
  return { ...entityFields(goal.id), description: null, userId: null, cloudId: null, deletedAt: null, ...goal }
}

/** A complete daily `Habit` for seeds, due since 1 January 2026. */
export function makeHabit(habit: Pick<Habit, 'id' | 'title' | 'lifeSpace'> & Partial<Habit>): Habit {
  return {
    ...entityFields(habit.id),
    notes: null,
    frequency: { kind: 'daily' },
    startDate: new Date(2026, 0, 1),
    rewardId: null,
    targetMinutes: null,
    pausedAt: null,
    sortOrder: 0,
    userId: null,
    cloudId: null,
    deletedAt: null,
    ...habit,
  }
}

/** Ein vollständiger `TimeEntry` für Saaten; ohne `endTime` **läuft** der Abschnitt. */
export function makeTimeEntry(entry: Pick<TimeEntry, 'id' | 'taskId' | 'startTime'> & Partial<TimeEntry>): TimeEntry {
  return { ...entityFields(entry.id), endTime: null, userId: null, cloudId: null, deletedAt: null, ...entry }
}

/** Ein vollständiger `TaskComment` für Saaten; ohne `postedAt` zum Zeitpunkt 0. */
export function makeTaskComment(comment: Pick<TaskComment, 'id' | 'taskId' | 'content'> & Partial<TaskComment>): TaskComment {
  return { ...entityFields(comment.id), postedAt: new Date(0), importedFrom: null, userId: null, cloudId: null, deletedAt: null, ...comment }
}

/** Eine vollständige `TaskReminder` für Saaten; ohne weitere Angaben die Standard-Erinnerung. */
export function makeTaskReminder(reminder: Pick<TaskReminder, 'id' | 'taskId'> & Partial<TaskReminder>): TaskReminder {
  return { ...reminderFromDraft(reminder.taskId, STANDARD_REMINDER, new Date(0), reminder.id), ...reminder }
}

/** Ein vollständiger `ProjectComment` für Saaten; ohne `postedAt` zum Zeitpunkt 0. */
export function makeProjectComment(comment: Pick<ProjectComment, 'id' | 'projectId' | 'content'> & Partial<ProjectComment>): ProjectComment {
  return { ...entityFields(comment.id), postedAt: new Date(0), importedFrom: null, userId: null, cloudId: null, deletedAt: null, ...comment }
}

/** Ein Projekt-Kommentar aus einem Entwurf — wie `db.createProjectComment`. */
function projectCommentFromDraft(projectId: string, draft: CommentDraft, now: Date): ProjectComment {
  return makeProjectComment({
    id: crypto.randomUUID(), projectId, content: draft.content, postedAt: draft.postedAt ?? now,
    importedFrom: draft.importedFrom ?? null, createdAt: now, updatedAt: now, lastModified: now,
  })
}

/** Ein Kommentar aus einem Entwurf — wie `db.createComment`. */
function commentFromDraft(taskId: string, draft: CommentDraft, now: Date): TaskComment {
  return makeTaskComment({
    id: crypto.randomUUID(), taskId, content: draft.content, postedAt: draft.postedAt ?? now,
    importedFrom: draft.importedFrom ?? null, createdAt: now, updatedAt: now, lastModified: now,
  })
}

/** A complete `HabitLog` for seeds; `date` is the local day `YYYY-MM-DD`. */
export function makeHabitLog(log: Pick<HabitLog, 'id' | 'habitId' | 'date'> & Partial<HabitLog>): HabitLog {
  return { ...entityFields(log.id), completed: true, userId: null, cloudId: null, deletedAt: null, ...log }
}

/** A complete `AvatarCategory` for seeds. */
export function makeAvatarCategory(category: Pick<AvatarCategory, 'id' | 'name_de' | 'name_en' | 'icon_emoji'> & Partial<AvatarCategory>): AvatarCategory {
  return { sort_order: 0, is_active: true, ...category }
}

/** A complete preset `Avatar` for seeds; `file_path` holds the image URL. */
export function makeAvatar(avatar: Pick<Avatar, 'id' | 'name' | 'category_id' | 'file_path'> & Partial<Avatar>): Avatar {
  return { is_preset: true, is_active: true, tags_de: [], tags_en: [], ...avatar }
}

const byName = (a: Project, b: Project) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)

/* ── Fokuszeit ──────────────────────────────────────────────────────────────
   Dieselben Regeln wie in Dexie (`indexeddb.ts`), nur auf einem Array: höchstens
   ein laufender Abschnitt, und er ist immer der jüngste. */

function runningFocus(entries: TimeEntry[]): TimeEntry | null {
  const newest = entries
    .filter(e => !e.deletedAt)
    .reduce<TimeEntry | null>((best, e) => (!best || e.startTime > best.startTime ? e : best), null)
  return newest && newest.endTime === null ? newest : null
}

/** Bucht den laufenden Abschnitt auf `at` — zu kurz wird verworfen statt gebucht. */
function closeRunningFocus(entries: TimeEntry[], at: Date): TimeEntry[] {
  const running = runningFocus(entries)
  if (!running) return entries
  const seconds = (at.getTime() - running.startTime.getTime()) / 1000
  if (seconds < MIN_FOCUS_SECONDS) return entries.filter(e => e.id !== running.id)
  return entries.map(e => (e.id === running.id ? { ...e, endTime: at } : e))
}

/** Gebuchte Sekunden je Aufgabe; jede genannte Aufgabe steht drin, auch mit `0`. */
function focusSecondsByTask(entries: TimeEntry[], taskIds: string[]): Map<string, number> {
  const seconds = new Map<string, number>()
  for (const id of taskIds) seconds.set(id, 0)
  for (const entry of entries) {
    if (!entry.endTime || entry.deletedAt || !seconds.has(entry.taskId)) continue
    const elapsed = (entry.endTime.getTime() - entry.startTime.getTime()) / 1000
    seconds.set(entry.taskId, (seconds.get(entry.taskId) ?? 0) + Math.max(0, Math.round(elapsed)))
  }
  return seconds
}

/** Zahl bei „Demnächst“ eines Bereichs — dieselbe Regel wie `db.getPlannedThisWeek`. */
function upcomingCountOf(tasks: readonly Task[], lifeSpace: LifeSpace): number {
  return tasks.filter(t => t.lifeSpace === lifeSpace && isPlannedWithinNextWeek(t)
    && (!t.parentTaskId || chainKeepsChildListed(ancestorsOf(tasks, t.id)))).length
}

/**
 * A data source that lives in memory — for Storybook and tests. Writes replace
 * the state object, so readers re-render and unchanged lists keep their identity.
 * Mirrors the Dexie source without sync bookkeeping. Seeded `tasks` feed what components and
 * pages read themselves.
 *
 * Rows are never changed in place: every write builds new row objects (`{ ...t, … }`). The mobile app
 * relies on it — its SQLite storage (`apps/mobile/src/data/storage/diff.ts`) skips rows whose object
 * did not change.
 *
 * @example
 * const source = createMemoryDataSource({ tags: [makeTag({ id: 't1', name: 'Garten' })] })
 * <DataSourceProvider source={source}><TagDropdownContent … /></DataSourceProvider>
 */
export function createMemoryDataSource(seed: MemoryData = {}, options: MemoryOptions = {}): DataSource {
  let state: MemoryState = {
    tags: seed.tags ?? [],
    rewardUsages: seed.rewardUsages ?? [],
    projects: seed.projects ?? [],
    canvasNotes: seed.canvasNotes ?? [],
    profile: seed.profile ?? null,
    tasks: [
      ...(seed.tasks ?? []),
      // Gesäte Checklisten-Zeilen werden, was sie in Dexie sind: Aufgaben mit Elternteil.
      ...(seed.subtasks ?? []).map(sub => ({
        ...childTask(seed.tasks?.find(t => t.id === sub.taskId), {
          id: sub.id, parentId: sub.taskId, title: sub.title, done: sub.done, order: sub.sortOrder, importedFrom: sub.importedFrom,
        }),
        deletedAt: sub.deletedAt,
        cloudId: sub.cloudId,
        userId: sub.userId,
      })),
    ],
    sections: seed.sections ?? [],
    settings: seed.settings ?? {},
    goals: seed.goals ?? [],
    habits: seed.habits ?? [],
    habitLogs: seed.habitLogs ?? [],
    timeEntries: seed.timeEntries ?? [],
    taskComments: seed.taskComments ?? [],
    projectComments: seed.projectComments ?? [],
    taskReminders: seed.taskReminders ?? [],
    preferences: seed.preferences ?? {},
    avatarCatalog: seed.avatarCatalog ?? { categories: [], avatars: [] },
  }
  const listeners = new Set<() => void>()

  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  }
  const getState = () => state
  const setState = (next: Partial<MemoryState>) => {
    const updated = { ...state, ...next }
    options.onChange?.(updated, state)
    state = updated
    listeners.forEach(listener => listener())
  }
  const useMemoryState = () => useSyncExternalStore(subscribe, getState, getState)
  /*
    Ohne Kennung hört eine Abfrage nicht zu — wie bei Dexie, wo sie dann nichts liest. Sonst renderte im
    `TaskRowDataScope` jede Zeile bei jeder Änderung neu, obwohl sie ihre Daten aus dem Bündel nimmt (#303).
  */
  const idle = () => () => {}
  const useMemoryStateIf = (active: boolean) => useSyncExternalStore(active ? subscribe : idle, getState, getState)

  /** Zeile → die Zeilen darunter, die ihr Abhaken mitgenommen hat — wie `db.coupleCompletion`. */
  const coupledChildren = new Map<string, string[]>()

  /** **Erledigt-Zähler** und **Vertagen** (#198) — wie `db.taskCounter`. */
  const taskCounter = createTaskCounter()
  const withCounters = (row: Task | undefined, changes: Partial<Task>, now?: Date): Partial<Task> =>
    (row ? { ...changes, ...taskCounter.count(row, changes, now) } : changes)

  /** Abhaken wie in Todoist (T14 #179, Grill 5), auf jeder Ebene (#181), auf dem Stand NACH der Änderung von `id`. */
  const coupleCompletion = (tasks: Task[], id: string, statusBefore: Task['status']): Task[] => {
    const task = tasks.find(t => t.id === id)
    if (!task || task.status === statusBefore) return tasks
    const setDone = (list: Task[], ids: string[], done: boolean, at: Date | null) =>
      list.map(t => (ids.includes(t.id) ? { ...t, ...withCounters(t, { status: done ? 'completed' : 'todo', completedAt: done ? at : null }) } : t))
    if (task.status === 'completed') {
      const open = descendantsOf(tasks, id).filter(c => c.status !== 'completed').map(c => c.id)
      coupledChildren.set(id, open)
      const at = task.completedAt ?? new Date()
      const below = setDone(tasks, open, true, at)
      // #217: Das letzte Kind schließt die Eltern-Zeile mit, bis Ebene 1 — wie `db.coupleCompletion`.
      const above = ancestorsCompletedBy(below, id)
      above.forEach(ancestorId => coupledChildren.set(ancestorId, []))
      return setDone(below, above, true, at)
    }
    const taken = (coupledChildren.get(id) ?? []).filter(cid => tasks.some(t => t.id === cid && !t.deletedAt && t.status === 'completed'))
    coupledChildren.delete(id)
    const chain: string[] = []
    for (const ancestor of ancestorsOf(tasks, id)) {
      if (ancestor.deletedAt || ancestor.status !== 'completed') continue
      if (ancestor.recurrence) break
      coupledChildren.delete(ancestor.id)
      chain.push(ancestor.id)
    }
    return setDone(tasks, [...taken, ...chain], false, null)
  }

  /** Lebende Projekte eines Lebensbereichs ohne System-Projekte — die Zeilen des Sidebar-Baums (#185). */
  const liveProjectsOf = (lifeSpace: string) => state.projects.filter(p => p.lifeSpace === lifeSpace && !p.deletedAt && !p.isSystem)

  /** Die lebenden Unterprojekte unter `id`, beliebig tief — wie `db.liveSubprojectIds`. */
  const liveSubprojectIds = (id: string): string[] => {
    const out: string[] = []
    const queue = [id]
    const seen = new Set(queue)
    while (queue.length > 0) {
      const parent = queue.shift()!
      for (const child of state.projects.filter(p => p.parentId === parent && !p.deletedAt)) {
        if (seen.has(child.id)) continue
        seen.add(child.id)
        out.push(child.id)
        queue.push(child.id)
      }
    }
    return out
  }

  const withTreeChanges = (projects: Project[], changes: readonly ProjectTreeChange[]): Project[] => {
    if (!changes.length) return projects
    const byId = new Map(changes.map(c => [c.id, c]))
    const now = new Date()
    return projects.map(p => (byId.has(p.id) ? { ...p, order: byId.get(p.id)!.order, parentId: byId.get(p.id)!.parentId, updatedAt: now } : p))
  }

  /** Wie `db.subprojectPlacement`: letztes Kind unter `parentId`, auf Ebene 4 direkt dahinter. */
  const subprojectPlacement = (id: string, parentId: string, lifeSpace: string) => {
    const projects = liveProjectsOf(lifeSpace)
    if (!projects.some(p => p.id === parentId)) return null
    const rows = flattenProjectTree(projects)
    const holds = canHoldSubproject(projects, parentId)
    const parent = holds ? parentId : rows.find(r => r.id === parentId)?.parentId ?? null
    const afterId = holds ? rows.filter(r => r.parentId === parentId).at(-1)?.id ?? null : parentId
    const changes = applyProjectMove([...projects, { id, order: Number.MAX_SAFE_INTEGER, parentId: null }], id, parent, afterId)
    const own = changes.find(c => c.id === id)
    return own ? { order: own.order, parentId: own.parentId, others: changes.filter(c => c.id !== id) } : null
  }

  /** Wie `db.deleteTask`: die Zeile samt Ast (#181), ihre Fokuszeit und die Verbindung ihrer Notizen. */
  const deleteBranch = (id: string): TaskCascade => {
    const deletedAt = new Date()
    const subtaskIds = descendantsOf(state.tasks, id).map(t => t.id)
    const branch = new Set([id, ...subtaskIds])
    const unlinkedNoteIds = state.canvasNotes.filter(n => n.taskId === id && !n.deletedAt).map(n => n.id)
    // Läuft der Fokus AN DIESEM Ast, wird er vorher gebucht; woanders bleibt er stehen.
    const running = runningFocus(state.timeEntries)
    const afterClose = running?.taskId && branch.has(running.taskId) ? closeRunningFocus(state.timeEntries, deletedAt) : state.timeEntries
    const timeEntryIds = afterClose.filter(e => e.taskId && branch.has(e.taskId) && !e.deletedAt).map(e => e.id)
    const commentIds = state.taskComments.filter(c => branch.has(c.taskId) && !c.deletedAt).map(c => c.id)
    const reminderIds = state.taskReminders.filter(r => branch.has(r.taskId) && !r.deletedAt).map(r => r.id)
    setState({
      tasks: state.tasks.map(t => (branch.has(t.id) ? { ...t, deletedAt } : t)),
      canvasNotes: state.canvasNotes.map(n => (unlinkedNoteIds.includes(n.id) ? { ...n, taskId: null } : n)),
      timeEntries: afterClose.map(e => (timeEntryIds.includes(e.id) ? { ...e, deletedAt } : e)),
      taskComments: state.taskComments.map(c => (commentIds.includes(c.id) ? { ...c, deletedAt } : c)),
      taskReminders: state.taskReminders.map(r => (reminderIds.includes(r.id) ? { ...r, deletedAt } : r)),
    })
    return { subtaskIds, unlinkedNoteIds, timeEntryIds, commentIds, reminderIds }
  }

  /** Rückweg von {@link deleteBranch}: genau die gemeldeten Zeilen, nichts, was vorher schon weg war. */
  const restoreBranch = (id: string, cascade: TaskCascade | undefined, tasks: Task[] = state.tasks) => {
    const ids = new Set([id, ...(cascade?.subtaskIds ?? [])])
    setState({
      tasks: tasks.map(t => (ids.has(t.id) ? { ...t, deletedAt: null } : t)),
      ...(cascade && {
        canvasNotes: state.canvasNotes.map(n => (cascade.unlinkedNoteIds.includes(n.id) ? { ...n, taskId: id } : n)),
        timeEntries: state.timeEntries.map(e => ((cascade.timeEntryIds ?? []).includes(e.id) ? { ...e, deletedAt: null } : e)),
        taskComments: restoredComments(cascade.commentIds),
        taskReminders: restoredReminders(cascade.reminderIds ?? []),
      }),
    })
  }

  /** Die Erinnerungen mit genau diesen Kennungen wieder lebend. */
  const restoredReminders = (ids: readonly string[]) =>
    state.taskReminders.map(r => (ids.includes(r.id) ? { ...r, deletedAt: null } : r))

  /** Wie `db.addStandardReminderIfMissing`: für jede Aufgabe, die gerade eine Uhrzeit am Wann bekam. */
  const standardRemindersFor = (before: Task[], after: Task[], ids: readonly string[], now: Date): TaskReminder[] =>
    ids.filter(id => gainsStartTime(before.find(t => t.id === id) ?? null, after.find(t => t.id === id) ?? {}))
      .filter(id => !state.taskReminders.some(r => r.taskId === id && !r.deletedAt && isStandardReminder(r)))
      .map(id => reminderFromDraft(id, STANDARD_REMINDER, now))

  /** Die Kommentare mit genau diesen Kennungen wieder lebend. */
  const restoredComments = (ids: readonly string[]) =>
    state.taskComments.map(c => (ids.includes(c.id) ? { ...c, deletedAt: null } : c))

  /** Unter Ebene 4 hängt nichts mehr (#181) — wie `db.assertRoomBelow`. */
  const assertRoomBelow = (parentId: string) => {
    if (depthOf(state.tasks, parentId) >= MAX_SUBTASK_DEPTH) throw new Error(`Subtask depth: nothing below level ${MAX_SUBTASK_DEPTH}`)
  }

  /** Wie `db.writePlacements`: schreibt Plätze und löst die Kopplung zum alten Elternteil. */
  const writePlacements = (placements: TaskPlacement[]) => {
    const now = new Date()
    const byId = new Map(placements.map(p => [p.id, p]))
    for (const [parentId, taken] of coupledChildren) coupledChildren.set(parentId, taken.filter(id => !byId.has(id)))
    setState({ tasks: state.tasks.map(t => (byId.has(t.id) ? { ...t, ...byId.get(t.id), updatedAt: now, lastModified: now } : t)) })
  }

  return {
    useTags() {
      return useMemoryState().tags
    },

    async createTag(name, options) {
      const id = crypto.randomUUID()
      const tag = makeTag({ id, name: name.trim(), color: options?.color ?? nextTagColor(state.tags.length), importedFrom: options?.importedFrom ?? null })
      setState({ tags: [...state.tags, tag] })
      return tag
    },

    async getTags() {
      return state.tags.filter(tag => !tag.deletedAt)
    },

    async deleteUnusedTags(ids) {
      const used = new Set(state.tasks.filter(t => !t.deletedAt).flatMap(t => t.tags))
      const unused = ids.filter(id => !used.has(id))
      const now = new Date()
      setState({ tags: state.tags.map(tag => (unused.includes(tag.id) ? { ...tag, deletedAt: now } : tag)) })
      return unused
    },

    async findImported(source) {
      const prefix = `${source}:`
      const collect = (rows: ReadonlyArray<{ id: string; importedFrom?: string | null; deletedAt: Date | null }>) =>
        new Map(rows.filter(r => r.importedFrom?.startsWith(prefix)).map(r => [r.importedFrom!, { id: r.id, deleted: r.deletedAt != null }]))
      // Aufgaben-Zeilen bringen Dauer und Zähler mit — der Import trägt Tage (T32 #197) und Zähler (#198) nach.
      const collectTasks = (rows: readonly Task[]) =>
        new Map([...collect(rows)].map(([origin, row]) => {
          const task = rows.find(t => t.id === row.id)!
          return [origin, {
            ...row,
            estimatedDuration: task.estimatedDuration ?? null,
            completedCount: task.completedCount ?? null,
            postponedCount: task.postponedCount ?? null,
          }]
        }))
      const projects = new Map([...collect(state.projects)].map(([origin, row]) =>
        [origin, { ...row, lifeSpace: state.projects.find(p => p.id === row.id)!.lifeSpace }]))
      return {
        projects,
        sections: collect(state.sections),
        tasks: collectTasks(state.tasks.filter(t => t.parentTaskId == null)),
        subtasks: collectTasks(state.tasks.filter(t => t.parentTaskId != null)),
        tags: collect(state.tags),
        goals: collect(state.goals),
        habits: collect(state.habits),
      }
    },

    useSubtasks(taskId) {
      const { tasks } = useMemoryState()
      return useMemo(
        () => (taskId ? withoutSucceededRounds(orderedChildren(tasks, taskId), descendantsOf(tasks, taskId)).map(asSubtask) : undefined),
        [tasks, taskId],
      )
    },

    useSubtaskTree(taskId) {
      const { tasks } = useMemoryStateIf(!!taskId)
      return useMemo(
        () => (taskId
          ? treeEntriesOf(tasks, taskId, keepCurrentRounds(tasks, taskId)).map(({ task, depth, progress }) => ({ ...asSubtask(task), depth, progress }))
          : undefined),
        [tasks, taskId],
      )
    },

    useSubtaskTrees(taskIds) {
      const { tasks } = useMemoryState()
      const key = [...taskIds].sort().join(',')
      return useMemo(() => {
        // Einmal für alle Bäume statt je Aufgabe — sonst wächst die Rechnung mit Aufgaben × Zeilen.
        const children = childMap(tasks)
        return new Map((key ? key.split(',') : []).map(id => [id,
          children.has(id)
            ? treeEntriesOf(tasks, id, keepCurrentRounds(tasks, id, children), children)
              .map(({ task, depth, progress }) => ({ ...asSubtask(task), depth, progress }))
            : [],
        ]))
      }, [tasks, key])
    },

    async getSubtask(id) {
      const task = state.tasks.find(t => t.id === id && t.parentTaskId != null)
      return task ? asSubtask(task) : undefined
    },

    async createSubtask(taskId, title) {
      assertRoomBelow(taskId)
      const siblings = orderedChildren(state.tasks, taskId)
      const order = siblings.length === 0 ? 0 : Math.max(...siblings.map(s => s.order)) + 1
      const child = childTask(state.tasks.find(t => t.id === taskId), { id: crypto.randomUUID(), parentId: taskId, title, done: false, order })
      setState({ tasks: [...state.tasks, child] })
      return asSubtask(child)
    },

    async insertSubtaskAfter(taskId, afterId, title) {
      assertRoomBelow(taskId)
      const parent = state.tasks.find(t => t.id === taskId)
      const { ordered: siblings, all: basis } = orderedForWriting(state.tasks, taskId)
      const index = siblings.findIndex(s => s.id === afterId)
      // Die genannte Zeile gibt es nicht (mehr) — dann ist „dahinter" das Ende.
      if (index === -1) {
        const last = siblings[siblings.length - 1]
        const appended = childTask(parent, { id: crypto.randomUUID(), parentId: taskId, title, done: false, order: last ? last.order + 1 : 0 })
        setState({ tasks: [...state.tasks, appended] })
        return asSubtask(appended)
      }

      let order = orderBetween(siblings[index]!.order, siblings[index + 1]?.order)
      let renumbered = basis
      if (order === null) {
        renumbered = renumberIn(basis, siblings)
        order = orderBetween(index, index + 1)!
      }

      const child = childTask(parent, { id: crypto.randomUUID(), parentId: taskId, title, done: false, order })
      setState({ tasks: [...renumbered, child] })
      return asSubtask(child)
    },

    async moveSubtask(id, afterId) {
      const child = state.tasks.find(t => t.id === id)
      if (!child?.parentTaskId) return

      const { ordered, all: basis } = orderedForWriting(state.tasks, child.parentTaskId)
      const siblings = ordered.filter(s => s.id !== id)
      const index = afterId === null ? -1 : siblings.findIndex(s => s.id === afterId)
      if (afterId !== null && index === -1) return

      let order = orderBetween(siblings[index]?.order, siblings[index + 1]?.order)
      let renumbered = basis
      if (order === null) {
        renumbered = renumberIn(basis, siblings)
        order = orderBetween(index, index + 1)!
      }

      setState({ tasks: renumbered.map(s => (s.id === id ? { ...s, order: order! } : s)) })
    },

    async updateSubtask(id, changes) {
      const fields: Partial<Task> = {}
      if (changes.title !== undefined) fields.title = changes.title
      if (changes.sortOrder !== undefined) fields.order = changes.sortOrder
      if (changes.deletedAt !== undefined) fields.deletedAt = changes.deletedAt
      if (changes.done !== undefined) {
        fields.status = changes.done ? 'completed' : 'todo'
        fields.completedAt = changes.done ? new Date() : null
      }
      const before = state.tasks.find(t => t.id === id)?.status ?? 'todo'
      setState({ tasks: coupleCompletion(state.tasks.map(t => (t.id === id ? { ...t, ...withCounters(t, fields) } : t)), id, before) })
    },

    async deleteSubtask(id) {
      return deleteBranch(id)
    },

    async restoreSubtask(id, cascade) {
      restoreBranch(id, cascade)
    },

    async updateTask(id, changes) {
      const now = new Date()
      const row = state.tasks.find(t => t.id === id)
      const before = row?.status ?? 'todo'
      const tasks = state.tasks.map(t => (t.id === id ? { ...t, ...withCounters(t, changes, now), updatedAt: now, lastModified: now } : t))
      const { tasks: next, dropped } = dropChildRecurrence(coupleCompletion(withChildrenFollowing(tasks, new Map([[id, changes]])), id, before), state.tasks, changes.recurrence != null ? [id] : [])
      setState({ tasks: next, taskReminders: [...state.taskReminders, ...standardRemindersFor(state.tasks, next, [id], now)] })
      dropped.forEach(reportChildRecurrenceDropped)
    },

    async updateTasks(changes) {
      const now = new Date()
      const byId = new Map(changes.map(c => [c.id, c.changes]))
      const tasks = state.tasks.map(t => (byId.has(t.id) ? { ...t, ...withCounters(t, byId.get(t.id)!, now), updatedAt: now, lastModified: now } : t))
      const recurring = changes.filter(c => c.changes.recurrence != null).map(c => c.id)
      const { tasks: next, dropped } = dropChildRecurrence(withChildrenFollowing(tasks, byId), state.tasks, recurring)
      setState({ tasks: next, taskReminders: [...state.taskReminders, ...standardRemindersFor(state.tasks, next, [...byId.keys()], now)] })
      dropped.forEach(reportChildRecurrenceDropped)
    },

    async getTask(id) {
      return state.tasks.find(t => t.id === id) ?? null
    },

    async getTaskAncestors(id) {
      return ancestorsOf(state.tasks, id)
    },

    useTaskAncestors(id) {
      const { tasks } = useMemoryState()
      return useMemo(() => (id ? ancestorsOf(tasks, id) : []), [tasks, id])
    },

    useTask(id) {
      const { tasks } = useMemoryState()
      return useMemo(() => (id ? tasks.find(t => t.id === id) ?? null : null), [tasks, id])
    },

    useTasksByIds(ids) {
      const { tasks } = useMemoryState()
      const key = ids.join(',')
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return useMemo(() => new Map(tasks.filter(t => ids.includes(t.id)).map(t => [t.id, t])), [tasks, key])
    },

    useTaskChains(ids) {
      const { tasks } = useMemoryState()
      const key = ids.join(',')
      return useMemo(() => new Map(tasks.filter(t => ids.includes(t.id)).map(t => [t.id, [t, ...ancestorsOf(tasks, t.id)]])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tasks, key])
    },

    async createTask({ id, subtasks: drafts, comments: commentDrafts, reminders: reminderDrafts, ...data }) {
      const now = new Date()
      // Wie Dexie: hinter alle Aufgaben und hinter die Abschnitte des eigenen Projekts (#276).
      const order = data.order ?? Math.max(
        state.tasks.reduce((max, t) => Math.max(max, t.order), 0),
        ...(data.projectId ? state.sections.filter(s => s.projectId === data.projectId).map(s => s.order) : []),
      ) + 1
      const task = makeTask({
        ...data,
        id: id ?? crypto.randomUUID(),
        order,
        createdAt: now,
        updatedAt: now,
        lastModified: now,
      })
      // Wie Dexie: jede Ebene der Entwürfe (#181, Todoist-Import), in Vorordnung — samt Kommentaren (#182).
      const comments = (commentDrafts ?? []).map(draft => commentFromDraft(task.id, draft, now))
      // Wie Dexie: ohne mitgegebene Liste die Standard-Erinnerung, wenn das Wann eine Uhrzeit hat — auf jeder Ebene.
      const remindersOf = (owner: Task, drafts: ReminderDraft[] | undefined) =>
        (drafts ?? (gainsStartTime(null, owner) ? [STANDARD_REMINDER] : [])).map(draft => reminderFromDraft(owner.id, draft, now))
      const reminders = remindersOf(task, reminderDrafts)
      const branch = (parent: Task, list: SubtaskDraft[]): Task[] => list.flatMap((draft, order) => {
        const child = childTask(parent, { ...draft, id: crypto.randomUUID(), parentId: parent.id, order })
        comments.push(...(draft.comments ?? []).map(c => commentFromDraft(child.id, c, now)))
        reminders.push(...remindersOf(child, draft.reminders))
        return [child, ...branch(child, draft.subtasks ?? [])]
      })
      // Erst die Äste: Sie legen ihre Kommentare und Erinnerungen dazu.
      const children = branch(task, drafts ?? [])
      setState({ tasks: [...state.tasks, task, ...children], taskComments: [...state.taskComments, ...comments], taskReminders: [...state.taskReminders, ...reminders] })
      return task
    },

    async copySubtasks(fromTaskId, toTaskId, options = {}) {
      const target = state.tasks.find(t => t.id === toTaskId)
      const shift = (date: Date | null) => (date && options.shiftDays ? addDays(date, options.shiftDays) : date)
      // Wie Dexie: nur die Checkliste, keine erledigten Runden — und jede Ebene darunter (#181).
      const family = descendantsOf(state.tasks, fromTaskId)
      const copied = new Map<string, string>()
      const copyBranch = (fromId: string, toId: string): Task[] => withoutSucceededRounds(orderedChildren(state.tasks, fromId), family).flatMap(child => {
        const done = options.resetDone ? false : child.status === 'completed'
        const copy: Task = {
          ...child,
          id: crypto.randomUUID(),
          parentTaskId: toId,
          status: done ? 'completed' as const : 'todo' as const,
          completedAt: done ? child.completedAt : null,
          startDate: shift(child.startDate),
          dueDate: shift(child.dueDate),
          lifeSpace: target?.lifeSpace ?? child.lifeSpace,
          projectId: target?.projectId ?? null,
          sectionId: target?.sectionId ?? null,
          importedFrom: null,
          dayOrder: null,
          cloudId: null,
        }
        copied.set(child.id, copy.id)
        return [copy, ...copyBranch(child.id, copy.id)]
      })
      setState({ tasks: [...state.tasks, ...copyBranch(fromTaskId, toTaskId)] })
      return copied
    },

    async nestTasks(ids, parentId, afterId) {
      const plan = planNest(state.tasks, ids, parentId, afterId)
      if ('refused' in plan) return plan
      writePlacements(plan.after)
      return { before: plan.before }
    },

    async unnestTask(id, at = 'below') {
      const task = state.tasks.find(t => t.id === id)
      // Neben der Aufgabe ganz oben in der Kette (#181), nicht neben dem direkten Eltern-Teil.
      const top = task?.parentTaskId ? rootOf(state.tasks, id) : undefined
      const root = typeof at !== 'object' ? top : undefined
      const sectionOrder = root?.sectionId ? state.sections.find(sec => sec.id === root.sectionId)?.order : undefined
      // Der Baum ihrer Aufgabe: ihr Ast und ihre alten Runden ziehen mit.
      const family = top ? [top, ...descendantsOf(state.tasks, top.id)] : []
      const plan = planUnnest(task, at, root, root ? state.tasks.filter(t => sameList(root, t)) : [], sectionOrder, family)
      if (!plan) return null
      writePlacements(plan.after)
      return plan.before
    },

    async indentSubtasks(ids) {
      const plan = planIndent(state.tasks, ids)
      if ('refused' in plan) return plan
      writePlacements(plan.after)
      return { before: plan.before }
    },

    async outdentSubtasks(ids) {
      const plan = planOutdent(state.tasks, ids)
      if ('refused' in plan) return plan
      writePlacements(plan.after)
      return { before: plan.before }
    },

    async restorePlacements(placements) {
      writePlacements(placements)
    },

    async reorderTasks(updates) {
      const now = new Date()
      const byId = new Map(updates.map(u => [u.id, u]))
      setState({
        tasks: withChildrenFollowing(state.tasks.map(t => {
          const update = byId.get(t.id)
          if (!update) return t
          const sectionId = update.sectionId !== undefined ? update.sectionId : t.sectionId
          return { ...t, order: update.order, sectionId, updatedAt: now, lastModified: now }
        }), new Map(updates.filter(u => u.sectionId !== undefined).map(u => [u.id, { sectionId: u.sectionId }]))),
      })
    },

    async restoreTask(snapshot, cascade) {
      const restored = { ...snapshot, deletedAt: null }
      const exists = state.tasks.some(t => t.id === snapshot.id)
      restoreBranch(snapshot.id, cascade, exists ? state.tasks.map(t => (t.id === snapshot.id ? restored : t)) : [...state.tasks, restored])
    },

    useRunningFocus() {
      const { timeEntries } = useMemoryState()
      return useMemo(() => runningFocus(timeEntries), [timeEntries])
    },

    async startFocus(taskId) {
      const now = new Date()
      const entry = makeTimeEntry({ id: crypto.randomUUID(), taskId, startTime: now, createdAt: now, updatedAt: now, lastModified: now })
      // Erst den laufenden buchen, dann den neuen anlegen — ein Fokus zur Zeit.
      setState({ timeEntries: [...closeRunningFocus(state.timeEntries, now), entry] })
      return entry
    },

    async endFocus(at = new Date()) {
      setState({ timeEntries: closeRunningFocus(state.timeEntries, at) })
    },

    async addTimeEntry(taskId, startTime, endTime) {
      const now = new Date()
      const entry = makeTimeEntry({ id: crypto.randomUUID(), taskId, startTime, endTime, createdAt: now, updatedAt: now, lastModified: now })
      setState({ timeEntries: [...state.timeEntries, entry] })
      return entry
    },

    useFocusSeconds(taskIds) {
      const { timeEntries } = useMemoryState()
      const key = [...taskIds].sort().join(',')
      return useMemo(() => focusSecondsByTask(timeEntries, key ? key.split(',') : []), [timeEntries, key])
    },

    useTaskComments(taskId) {
      const { taskComments } = useMemoryState()
      return useMemo(
        () => (taskId
          ? taskComments.filter(c => c.taskId === taskId && !c.deletedAt).sort((a, b) => a.postedAt.getTime() - b.postedAt.getTime())
          : undefined),
        [taskComments, taskId],
      )
    },

    useCommentCount(taskId) {
      const { taskComments } = useMemoryStateIf(!!taskId)
      return useMemo(() => (taskId ? taskComments.filter(c => c.taskId === taskId && !c.deletedAt).length : 0), [taskComments, taskId])
    },

    useCommentCounts(taskIds) {
      const { taskComments } = useMemoryState()
      const key = [...taskIds].sort().join(',')
      return useMemo(() => {
        const counts = new Map<string, number>((key ? key.split(',') : []).map(id => [id, 0]))
        for (const c of taskComments) if (!c.deletedAt && counts.has(c.taskId)) counts.set(c.taskId, counts.get(c.taskId)! + 1)
        return counts
      }, [taskComments, key])
    },

    async createComment(taskId, draft) {
      const comment = commentFromDraft(taskId, draft, new Date())
      setState({ taskComments: [...state.taskComments, comment] })
      return comment
    },

    async updateComment(id, content) {
      const now = new Date()
      setState({ taskComments: state.taskComments.map(c => (c.id === id ? { ...c, content, updatedAt: now, lastModified: now } : c)) })
    },

    async deleteComment(id) {
      const deletedAt = new Date()
      setState({ taskComments: state.taskComments.map(c => (c.id === id && !c.deletedAt ? { ...c, deletedAt } : c)) })
    },

    async restoreComment(id) {
      setState({ taskComments: restoredComments([id]) })
    },

    useProjectComments(projectId) {
      const { projectComments } = useMemoryState()
      return useMemo(
        () => (projectId
          ? projectComments.filter(c => c.projectId === projectId && !c.deletedAt).sort((a, b) => a.postedAt.getTime() - b.postedAt.getTime())
          : undefined),
        [projectComments, projectId],
      )
    },

    useProjectCommentCount(projectId) {
      const { projectComments } = useMemoryState()
      return useMemo(() => (projectId ? projectComments.filter(c => c.projectId === projectId && !c.deletedAt).length : 0), [projectComments, projectId])
    },

    async createProjectComment(projectId, draft) {
      const comment = projectCommentFromDraft(projectId, draft, new Date())
      setState({ projectComments: [...state.projectComments, comment] })
      return comment
    },

    async updateProjectComment(id, content) {
      const now = new Date()
      setState({ projectComments: state.projectComments.map(c => (c.id === id ? { ...c, content, updatedAt: now, lastModified: now } : c)) })
    },

    async deleteProjectComment(id) {
      const deletedAt = new Date()
      setState({ projectComments: state.projectComments.map(c => (c.id === id && !c.deletedAt ? { ...c, deletedAt } : c)) })
    },

    async restoreProjectComment(id) {
      setState({ projectComments: state.projectComments.map(c => (c.id === id ? { ...c, deletedAt: null } : c)) })
    },

    async moveComments(moves) {
      setState({ taskComments: state.taskComments.map(c => (!c.deletedAt && moves.has(c.taskId) ? { ...c, taskId: moves.get(c.taskId)! } : c)) })
    },

    useTaskReminders(taskId) {
      const { taskReminders } = useMemoryStateIf(!!taskId)
      return useMemo(
        () => (taskId
          ? taskReminders.filter(r => r.taskId === taskId && !r.deletedAt).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
          : undefined),
        [taskReminders, taskId],
      )
    },

    useRemindersByTask(taskIds) {
      const { taskReminders } = useMemoryState()
      const key = [...taskIds].sort().join(',')
      return useMemo(() => {
        const byTask = new Map<string, TaskReminder[]>((key ? key.split(',') : []).map(id => [id, []]))
        const live = taskReminders.filter(r => !r.deletedAt).sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        for (const r of live) byTask.get(r.taskId)?.push(r)
        return byTask
      }, [taskReminders, key])
    },

    useLiveReminders() {
      const { taskReminders } = useMemoryState()
      return useMemo(() => taskReminders.filter(r => !r.deletedAt), [taskReminders])
    },

    async createReminder(taskId, draft) {
      const reminder = reminderFromDraft(taskId, draft, new Date())
      setState({ taskReminders: [...state.taskReminders, reminder] })
      return reminder
    },

    async updateReminder(id, changes) {
      const now = new Date()
      setState({ taskReminders: state.taskReminders.map(r => (r.id === id ? { ...r, ...changes, updatedAt: now, lastModified: now } : r)) })
    },

    async deleteReminder(id) {
      const deletedAt = new Date()
      setState({ taskReminders: state.taskReminders.map(r => (r.id === id && !r.deletedAt ? { ...r, deletedAt } : r)) })
    },

    async restoreReminder(id) {
      setState({ taskReminders: restoredReminders([id]) })
    },

    async moveReminders(moves) {
      setState({ taskReminders: state.taskReminders.map(r => (!r.deletedAt && moves.has(r.taskId) ? { ...r, taskId: moves.get(r.taskId)!, snoozedUntil: null } : r)) })
    },

    async copyReminders(copies) {
      const now = new Date()
      const copied = state.taskReminders
        .filter(r => !r.deletedAt && r.kind === 'relative' && copies.has(r.taskId))
        .map(r => reminderFromDraft(copies.get(r.taskId)!, { kind: 'relative', anchor: r.anchor ?? 'start', offsetMinutes: r.offsetMinutes }, now))
      setState({ taskReminders: [...state.taskReminders, ...copied] })
    },

    async recordRewardUsage(rewardId) {
      const usage = makeRewardUsage({ rewardId, usedAt: new Date() })
      setState({ rewardUsages: [...state.rewardUsages.filter(u => u.rewardId !== rewardId), usage] })
    },

    async getRecentRewardUsages(limit) {
      return state.rewardUsages
        .filter(u => !u.deletedAt)
        .sort((a, b) => b.usedAt.getTime() - a.usedAt.getTime())
        .slice(0, limit)
    },

    useProjects() {
      const { projects } = useMemoryState()
      return useMemo(() => projects.filter(p => !p.isSystem).sort(byName), [projects])
    },

    async getProjects() {
      return state.projects.filter(p => !p.isSystem && !p.deletedAt).sort(byName)
    },

    async searchTasks(query, options) {
      return searchTaskList(state.tasks, query, options)
    },

    async searchCanvasNotes(query, options) {
      return searchCanvasNoteList(state.canvasNotes, new Map(state.projects.map(p => [p.id, p])), query, options)
    },

    async latestTasks(options) {
      return latestTaskList(state.tasks, options)
    },

    async latestCanvasNotes(options) {
      return latestCanvasNoteList(state.canvasNotes, new Map(state.projects.map(p => [p.id, p])), options)
    },

    async latestFocusedTasks(options) {
      return latestFocusedTaskList(state.timeEntries, new Map(state.tasks.map(t => [t.id, t])), options)
    },

    useProject(id) {
      const { projects } = useMemoryState()
      return useMemo(() => (id ? projects.find(p => p.id === id) ?? null : null), [projects, id])
    },

    async updateProject(id, changes) {
      const now = new Date()
      setState({
        projects: state.projects.map(p => (
          p.id === id ? { ...p, ...titleConsistencyChanges(p, changes), ...changes, id, updatedAt: now } : p
        )),
      })
    },

    useProjectsByLifeSpace(lifeSpace) {
      const { projects } = useMemoryState()
      return useMemo(
        () => projects
          .filter(p => p.lifeSpace === lifeSpace && !p.deletedAt && !p.isSystem)
          .sort((a, b) => a.order - b.order),
        [projects, lifeSpace],
      )
    },

    useCanvasNotes(projectId) {
      const { canvasNotes } = useMemoryState()
      return useMemo(
        () => (projectId ? canvasNotes.filter(n => n.projectId === projectId && !n.deletedAt) : undefined),
        [canvasNotes, projectId],
      )
    },

    async createCanvasNote({ id, ...data }) {
      const now = new Date()
      const note = makeCanvasNote({ ...data, id: id ?? crypto.randomUUID() })
      const created = { ...note, createdAt: now, updatedAt: now, lastModified: now }
      setState({ canvasNotes: [...state.canvasNotes, created] })
      return created
    },

    async updateCanvasNote(id, changes) {
      const note = state.canvasNotes.find(n => n.id === id)
      if (!note) return null
      const now = new Date()
      const updated: CanvasNote = { ...note, ...changes, updatedAt: now, lastModified: now }
      setState({ canvasNotes: state.canvasNotes.map(n => (n === note ? updated : n)) })
      return updated
    },

    async setCanvasNoteMeasuredHeight(id, height) {
      setState({ canvasNotes: state.canvasNotes.map(n => (n.id === id ? { ...n, height } : n)) })
    },

    async setCanvasNoteMeasuredSize(id, size) {
      setState({ canvasNotes: state.canvasNotes.map(n => (n.id === id ? { ...n, ...size } : n)) })
    },

    async deleteCanvasNote(id) {
      setState({ canvasNotes: state.canvasNotes.map(n => (n.id === id ? { ...n, deletedAt: new Date() } : n)) })
    },

    async getCanvasNotesByIds(ids) {
      return ids.map(id => state.canvasNotes.find(n => n.id === id))
    },

    async deleteTask(id) {
      return deleteBranch(id)
    },

    async restoreTasks(ids) {
      setState({ tasks: state.tasks.map(t => (ids.includes(t.id) ? { ...t, deletedAt: null } : t)) })
    },

    useUserDisplayProfile() {
      const { profile } = useMemoryState()
      return useMemo<UserDisplayProfile>(() => ({
        fullName: profile?.fullName ?? null,
        avatarUrl: profile?.avatarUrl ?? null,
        avatarPresetId: profile?.avatarPresetId ?? null,
        email: null,
        isLoading: false,
      }), [profile])
    },

    async saveProfileName(fullName) {
      const trimmed = fullName.trim()
      if (!trimmed) return
      setState({ profile: { avatarUrl: null, ...state.profile, fullName: trimmed } })
    },

    async saveProfileAvatar(avatarPresetId) {
      const avatar = state.avatarCatalog.avatars.find(a => a.id === avatarPresetId)
      const avatarUrl = avatar ? avatar.cached_image_blob ?? avatar.file_path : null
      setState({ profile: { fullName: null, ...state.profile, avatarPresetId, avatarUrl } })
    },

    async loadAvatarCatalog() {
      return state.avatarCatalog
    },

    async searchAvatars(query, language) {
      const lower = query.toLowerCase()
      return state.avatarCatalog.avatars.filter(avatar =>
        avatar.name.toLowerCase().includes(lower)
        || (language === 'de' ? avatar.tags_de : avatar.tags_en)?.some(tag => tag.toLowerCase().includes(lower)))
    },

    avatarImageUrl(avatar) {
      return avatar.cached_image_blob ?? avatar.file_path
    },

    useSidebarCounts() {
      const { tasks } = useMemoryState()
      const day = useMidnightRefresh()
      const counts = useMemo(() => {
        const of = (space: LifeSpace) => ({
          upcomingCount: upcomingCountOf(tasks, space),
          quickNotesHasEntries: tasks.some(t => t.lifeSpace === space && isQuickNote(t)),
        })
        return { personal: of('personal'), work: of('work'), home: of('home') }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `day`: am Tageswechsel neu zählen, wie die Dexie-Quelle
      }, [tasks, day])
      return useStableSidebarCounts(counts)
    },

    useUpcomingTaskCount(lifeSpace) {
      const { tasks } = useMemoryState()
      const day = useMidnightRefresh()
      // eslint-disable-next-line react-hooks/exhaustive-deps -- `day`: am Tageswechsel neu zählen, wie die Dexie-Quelle
      return useMemo(() => upcomingCountOf(tasks, lifeSpace), [tasks, lifeSpace, day])
    },

    useTasksByProject(projectId) {
      const { tasks } = useMemoryState()
      return useMemo(
        () => (projectId ? tasks.filter(t => isProjectTask(t, projectId)).sort((a, b) => a.order - b.order) : undefined),
        [tasks, projectId],
      )
    },

    useSectionsByProject(projectId) {
      const { sections } = useMemoryState()
      return useMemo(
        () => sections
          .filter(s => s.projectId === projectId && !s.deletedAt && !s.archivedAt && s.title.trim() !== '')
          .sort((a, b) => a.order - b.order),
        [sections, projectId],
      )
    },

    useAllSectionsByProject(projectId) {
      const { sections } = useMemoryState()
      return useMemo(
        () => (projectId ? sections.filter(s => s.projectId === projectId && !s.deletedAt).sort((a, b) => a.order - b.order) : undefined),
        [sections, projectId],
      )
    },

    async getSection(id) {
      return state.sections.find(s => s.id === id) ?? null
    },

    async getTasksByProject(projectId) {
      return state.tasks.filter(t => isProjectTask(t, projectId)).sort((a, b) => a.order - b.order)
    },

    async getSectionsByProject(projectId) {
      return state.sections.filter(s => s.projectId === projectId && !s.deletedAt).sort((a, b) => a.order - b.order)
    },

    async getQuickNotes(lifeSpace) {
      return state.tasks.filter(t => t.lifeSpace === lifeSpace && isQuickNote(t)).sort((a, b) => a.order - b.order)
    },

    async createSection(data) {
      const items = [
        ...state.tasks.filter(t => t.projectId === data.projectId && !t.deletedAt),
        ...state.sections.filter(s => s.projectId === data.projectId && !s.deletedAt),
      ]
      const order = data.order ?? (items.length ? Math.max(...items.map(i => i.order)) + 1 : 0)
      const now = new Date()
      const section = makeSection({ ...data, id: crypto.randomUUID(), order, createdAt: now, updatedAt: now, lastModified: now })
      setState({ sections: [...state.sections, section] })
      return section
    },

    async updateSection(id, changes) {
      setState({ sections: state.sections.map(s => (s.id === id ? { ...s, ...changes, id } : s)) })
    },

    async deleteSection(id) {
      const taskIds = state.tasks.filter(t => t.sectionId === id && !t.deletedAt).map(t => t.id)
      setState({
        sections: state.sections.map(s => (s.id === id ? { ...s, deletedAt: new Date() } : s)),
        tasks: state.tasks.map(t => (taskIds.includes(t.id) ? { ...t, sectionId: null } : t)),
      })
      return { taskIds }
    },

    async restoreSection(snapshot, taskIds) {
      const restored = { ...snapshot, deletedAt: null }
      const exists = state.sections.some(s => s.id === snapshot.id)
      setState({
        sections: exists ? state.sections.map(s => (s.id === snapshot.id ? restored : s)) : [...state.sections, restored],
        tasks: state.tasks.map(t => (taskIds.includes(t.id) ? { ...t, sectionId: snapshot.id } : t)),
      })
    },

    async archiveSection(id) {
      const now = new Date()
      const completedTaskIds = state.tasks
        .filter(t => t.sectionId === id && !t.deletedAt && t.status !== 'completed')
        .map(t => t.id)
      setState({
        sections: state.sections.map(s => (s.id === id ? { ...s, archivedAt: now } : s)),
        tasks: state.tasks.map(t => (completedTaskIds.includes(t.id) ? { ...t, status: 'completed', completedAt: now } : t)),
      })
      return { completedTaskIds }
    },

    async unarchiveSection(id, reopen = []) {
      const section = state.sections.find(s => s.id === id)
      const reopenedTaskIds = state.tasks
        .filter(t => (reopen === 'closedByArchive'
          ? !!section && isClosedByArchive(t, section)
          : reopen.includes(t.id) && !t.deletedAt && t.status === 'completed'))
        .map(t => t.id)
      setState({
        sections: state.sections.map(s => (s.id === id ? { ...s, archivedAt: null } : s)),
        tasks: state.tasks.map(t => (reopenedTaskIds.includes(t.id) ? { ...t, status: 'todo', completedAt: null } : t)),
      })
      return { reopenedTaskIds }
    },

    async createProject({ comments = [], parentId = null, ...data }) {
      const siblings = state.projects.filter(p => p.lifeSpace === data.lifeSpace && !p.deletedAt)
      const now = new Date()
      const id = crypto.randomUUID()
      const placement = parentId ? subprojectPlacement(id, parentId, data.lifeSpace) : null
      const order = placement?.order ?? data.order ?? (siblings.length ? Math.min(...siblings.map(p => p.order)) : 0) - 1
      const project = makeProject({ ...data, id, order, parentId: placement?.parentId ?? null, createdAt: now, updatedAt: now, lastModified: now })
      setState({
        projects: withTreeChanges([...state.projects, project], placement?.others ?? []),
        projectComments: [...state.projectComments, ...comments.map(draft => projectCommentFromDraft(project.id, draft, now))],
      })
      return project
    },

    async getProjectIsEmpty(id) {
      const live = (row: { deletedAt?: Date | null }) => !row.deletedAt
      return !state.tasks.some(t => t.projectId === id && live(t))
        && !state.sections.some(s => s.projectId === id && live(s))
        && !state.projects.some(p => p.parentId === id && live(p))
        && !state.canvasNotes.some(n => n.projectId === id && live(n))
        && !state.projectComments.some(c => c.projectId === id && live(c))
    },

    async deleteProject(id) {
      const deletedAt = new Date()
      const subprojectIds = liveSubprojectIds(id)
      const projectIds = new Set([id, ...subprojectIds])
      const sectionIds = new Set(state.sections.filter(s => projectIds.has(s.projectId) && !s.deletedAt).map(s => s.id))
      const takes = (t: Task) => ((t.projectId != null && projectIds.has(t.projectId)) || (t.sectionId != null && sectionIds.has(t.sectionId))) && !t.deletedAt
      const takenTaskIds = state.tasks.filter(takes).map(t => t.id)
      const takenNoteIds = state.canvasNotes.filter(n => projectIds.has(n.projectId) && !n.deletedAt).map(n => n.id)
      const takenCommentIds = state.taskComments.filter(c => takenTaskIds.includes(c.taskId) && !c.deletedAt).map(c => c.id)
      const takenProjectCommentIds = state.projectComments.filter(c => projectIds.has(c.projectId) && !c.deletedAt).map(c => c.id)
      const takenReminderIds = state.taskReminders.filter(r => takenTaskIds.includes(r.taskId) && !r.deletedAt).map(r => r.id)
      setState({
        projects: state.projects.map(p => (projectIds.has(p.id) && !p.deletedAt ? { ...p, deletedAt } : p)),
        sections: state.sections.map(s => (sectionIds.has(s.id) ? { ...s, deletedAt } : s)),
        tasks: state.tasks.map(t => (takes(t) ? { ...t, deletedAt } : t)),
        canvasNotes: state.canvasNotes.map(n => (takenNoteIds.includes(n.id) ? { ...n, deletedAt } : n)),
        taskComments: state.taskComments.map(c => (takenCommentIds.includes(c.id) ? { ...c, deletedAt } : c)),
        projectComments: state.projectComments.map(c => (takenProjectCommentIds.includes(c.id) ? { ...c, deletedAt } : c)),
        taskReminders: state.taskReminders.map(r => (takenReminderIds.includes(r.id) ? { ...r, deletedAt } : r)),
      })
      return {
        taskIds: takenTaskIds, sectionIds: [...sectionIds], canvasNoteIds: takenNoteIds,
        commentIds: takenCommentIds, projectCommentIds: takenProjectCommentIds, reminderIds: takenReminderIds,
        subprojectIds,
      }
    },

    async restoreProject(id, cascade) {
      const projectIds = new Set([id, ...(cascade.subprojectIds ?? [])])
      setState({
        projects: state.projects.map(p => (projectIds.has(p.id) ? { ...p, deletedAt: null } : p)),
        sections: state.sections.map(s => (cascade.sectionIds.includes(s.id) ? { ...s, deletedAt: null } : s)),
        tasks: state.tasks.map(t => (cascade.taskIds.includes(t.id) ? { ...t, deletedAt: null } : t)),
        canvasNotes: state.canvasNotes.map(n => (cascade.canvasNoteIds.includes(n.id) ? { ...n, deletedAt: null } : n)),
        taskComments: restoredComments(cascade.commentIds),
        projectComments: state.projectComments.map(c => ((cascade.projectCommentIds ?? []).includes(c.id) ? { ...c, deletedAt: null } : c)),
        taskReminders: restoredReminders(cascade.reminderIds ?? []),
      })
    },

    async moveProjectInTree(projectId, { parentId, afterId }) {
      const project = state.projects.find(p => p.id === projectId && !p.deletedAt)
      if (!project) return false
      const projects = liveProjectsOf(project.lifeSpace)
      if (parentId !== null) {
        if (!projects.some(p => p.id === parentId)) return false
        if (parentId === projectId || projectDescendantIds(projects, projectId).includes(parentId)) return false
        if (projectDepth(projects, parentId) + 1 + projectSubtreeHeight(projects, projectId) > MAX_PROJECT_DEPTH) return false
      }
      const changes = applyProjectMove(projects, projectId, parentId, afterId)
      if (changes.length) setState({ projects: withTreeChanges(state.projects, changes) })
      return true
    },

    async moveProjectToLifeSpace(projectId, lifeSpace) {
      const project = state.projects.find(p => p.id === projectId)
      if (!project || project.lifeSpace === lifeSpace) return null
      const subprojectIds = projectDescendantIds(liveProjectsOf(project.lifeSpace), projectId)
      const branch = [projectId, ...subprojectIds]
      const sectionIds = new Set(state.sections.filter(s => branch.includes(s.projectId)).map(s => s.id))
      const moves = (t: Task) => ((t.projectId != null && branch.includes(t.projectId)) || (t.sectionId != null && sectionIds.has(t.sectionId))) && t.lifeSpace !== lifeSpace
      const siblings = liveProjectsOf(lifeSpace)
      const top = (siblings.length ? Math.min(...siblings.map(p => p.order)) : 1) - 1 - subprojectIds.length
      const now = new Date()
      const move = {
        projectId,
        from: { lifeSpace: project.lifeSpace, order: project.order, parentId: project.parentId ?? null },
        subprojects: subprojectIds.map(id => {
          const sub = state.projects.find(p => p.id === id)!
          return { id, lifeSpace: sub.lifeSpace, order: sub.order }
        }),
        tasks: state.tasks.filter(moves).map(t => ({ id: t.id, lifeSpace: t.lifeSpace })),
      }
      setState({
        projects: state.projects.map(p => (
          p.id === projectId ? { ...p, lifeSpace, order: top, parentId: null, updatedAt: now }
            : subprojectIds.includes(p.id) ? { ...p, lifeSpace, order: top + 1 + subprojectIds.indexOf(p.id), updatedAt: now }
              : p
        )),
        tasks: state.tasks.map(t => (moves(t) ? { ...t, lifeSpace, updatedAt: now } : t)),
      })
      return move
    },

    async restoreProjectLifeSpace(move) {
      const back = new Map(move.tasks.map(t => [t.id, t.lifeSpace]))
      const subs = new Map((move.subprojects ?? []).map(s => [s.id, s]))
      const now = new Date()
      setState({
        projects: state.projects.map(p => (
          p.id === move.projectId ? { ...p, ...move.from, updatedAt: now }
            : subs.has(p.id) ? { ...p, lifeSpace: subs.get(p.id)!.lifeSpace, order: subs.get(p.id)!.order, updatedAt: now }
              : p
        )),
        tasks: state.tasks.map(t => (back.has(t.id) ? { ...t, lifeSpace: back.get(t.id)!, updatedAt: now } : t)),
      })
    },

    useLifeSpaceProjects(lifeSpace) {
      const { projects } = useMemoryState()
      return useMemo(() => projects.filter(p => p.lifeSpace === lifeSpace && !p.deletedAt), [projects, lifeSpace])
    },

    useQuickNotes(lifeSpace) {
      const { tasks } = useMemoryState()
      return useMemo(
        () => tasks.filter(t => t.lifeSpace === lifeSpace && isQuickNote(t)).sort((a, b) => a.order - b.order),
        [tasks, lifeSpace],
      )
    },

    usePausedTasks() {
      const { tasks } = useMemoryState()
      return useMemo(() => tasks.filter(isDormantTask), [tasks])
    },

    useOpenTasks() {
      const { tasks } = useMemoryState()
      return useMemo(() => tasks.filter(task => !task.deletedAt && isOpenTask(task)), [tasks])
    },

    useFavoriteTasks() {
      const { tasks } = useMemoryState()
      return useMemo(() => tasks.filter(isFavoriteTask), [tasks])
    },

    useRecurringTasks() {
      const { tasks } = useMemoryState()
      return useMemo(() => tasks.filter(isRecurringTask), [tasks])
    },

    useCompletedTasks() {
      const { tasks } = useMemoryState()
      return useMemo(() => tasks.filter(isCompletedTask), [tasks])
    },

    useTasksInDateRange(from, to) {
      const { tasks } = useMemoryState()
      const fromMs = from.getTime()
      const toMs = to.getTime()
      return useMemo(() => tasks.filter(t => isInDateRange(t, new Date(fromMs), new Date(toMs))), [tasks, fromMs, toMs])
    },

    useGoals(lifeSpace) {
      const { goals } = useMemoryState()
      return useMemo(
        () => goals.filter(g => g.lifeSpace === lifeSpace && !g.deletedAt).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        [goals, lifeSpace],
      )
    },

    async createGoal(data) {
      const now = new Date()
      const goal = makeGoal({ ...data, id: crypto.randomUUID(), createdAt: now, updatedAt: now, lastModified: now })
      setState({ goals: [...state.goals, goal] })
      return goal
    },

    async updateGoal(id, changes) {
      setState({ goals: state.goals.map(g => (g.id === id ? { ...g, ...changes, id, updatedAt: new Date() } : g)) })
    },

    async deleteGoal(id) {
      setState({ goals: state.goals.map(g => (g.id === id ? { ...g, deletedAt: new Date() } : g)) })
    },

    async restoreGoal(id) {
      setState({ goals: state.goals.map(g => (g.id === id ? { ...g, deletedAt: null } : g)) })
    },

    useHabits(lifeSpace) {
      const { habits } = useMemoryState()
      return useMemo(
        () => habits.filter(h => h.lifeSpace === lifeSpace && !h.deletedAt).sort((a, b) => a.sortOrder - b.sortOrder),
        [habits, lifeSpace],
      )
    },

    useHabitLogs(habitIds) {
      const { habitLogs } = useMemoryState()
      const key = [...habitIds].sort().join(',')
      return useMemo(() => {
        const map = new Map<string, HabitLog[]>()
        for (const id of key ? key.split(',') : []) map.set(id, [])
        for (const log of habitLogs) if (!log.deletedAt) map.get(log.habitId)?.push(log)
        return map
      }, [habitLogs, key])
    },

    async createHabit(data) {
      const now = new Date()
      const siblings = state.habits.filter(h => h.lifeSpace === data.lifeSpace && !h.deletedAt)
      const sortOrder = data.sortOrder ?? (siblings.length ? Math.max(...siblings.map(h => h.sortOrder)) + 1 : 0)
      const habit = makeHabit({
        ...data,
        id: crypto.randomUUID(),
        startDate: data.startDate ?? new Date(now.getFullYear(), now.getMonth(), now.getDate()),
        sortOrder,
        createdAt: now,
        updatedAt: now,
        lastModified: now,
      })
      setState({ habits: [...state.habits, habit] })
      return habit
    },

    async updateHabit(id, changes) {
      setState({ habits: state.habits.map(h => (h.id === id ? { ...h, ...changes, id, updatedAt: new Date() } : h)) })
    },

    async deleteHabit(id) {
      setState({ habits: state.habits.map(h => (h.id === id ? { ...h, deletedAt: new Date() } : h)) })
    },

    async setHabitLog(habitId, date, completed) {
      const existing = state.habitLogs.find(l => l.habitId === habitId && l.date === date)
      setState({
        habitLogs: existing
          ? state.habitLogs.map(l => (l === existing ? { ...l, completed, deletedAt: null } : l))
          : [...state.habitLogs, makeHabitLog({ id: crypto.randomUUID(), habitId, date, completed })],
      })
    },

    usePreference<T>(key: string, defaultValue: T) {
      const { preferences } = useMemoryState()
      const value = (key in preferences ? preferences[key] : defaultValue) as T
      const setValue = useCallback((next: T) => {
        setState({ preferences: { ...state.preferences, [key]: next } })
      }, [key])
      return [value, setValue, true] as [T, (value: T) => void, boolean]
    },

    useSetting<T>(key: string, defaultValue: T) {
      const { settings } = useMemoryState()
      const value = (key in settings ? settings[key] : defaultValue) as T
      const setValue = useCallback(async (next: T) => {
        setState({ settings: { ...state.settings, [key]: next } })
      }, [key])
      return { value, setValue, loaded: true }
    },

    async getSetting<T>(key: string, defaultValue: T) {
      return (key in state.settings ? state.settings[key] : defaultValue) as T
    },

    async setSetting<T>(key: string, value: T) {
      setState({ settings: { ...state.settings, [key]: value } })
    },

    // Einstellungen und Vorlieben liegen hier als Karten; fürs Backup werden sie Zeilen wie in Dexie.
    useAppIsEmpty() {
      const { tasks, projects, goals, habits } = useMemoryState()
      return useMemo(() => !tasks.some(t => !t.deletedAt && !isIntroContent(t))
        && !projects.some(p => !p.deletedAt && !p.isSystem && !isIntroContent(p))
        && !goals.some(g => !g.deletedAt)
        && !habits.some(h => !h.deletedAt), [tasks, projects, goals, habits])
    },

    async readBackupRows(options) {
      const keep = <T extends { deletedAt?: Date | null }>(rows: T[]) => (options?.includeDeleted ? rows : rows.filter(row => !row.deletedAt))
      const at = new Date(0)
      const stamps = { createdAt: at, updatedAt: at, lastModified: at, synced: true, syncedAt: null }
      return {
        tables: {
          tags: keep(state.tags),
          projects: keep(state.projects.filter(p => !p.isSystem)),
          sections: keep(state.sections),
          tasks: keep(state.tasks),
          canvasNotes: keep(state.canvasNotes),
          taskComments: keep(state.taskComments),
          projectComments: keep(state.projectComments),
          taskReminders: keep(state.taskReminders),
          timeEntries: keep(options?.includeDeleted ? state.timeEntries : state.timeEntries.filter(e => e.endTime != null)),
          goals: keep(state.goals),
          habits: keep(state.habits),
          habitLogs: keep(state.habitLogs),
          settings: Object.entries(state.settings).map(([key, value]) => ({
            ...stamps, id: `setting:${key}`, key, value, type: 'string' as const, description: null, isUserSetting: true,
          })),
          userPreferences: Object.entries(state.preferences).map(([key, value]) => ({
            ...stamps, id: key, key, value, userId: null, cloudId: null, deletedAt: null,
          })),
          rewardUsages: keep(state.rewardUsages),
        },
        profile: state.profile
          ? { fullName: state.profile.fullName, displayName: null, email: null, avatarPresetId: state.profile.avatarPresetId ?? null }
          : null,
      }
    },

    async writeBackupRows(rows) {
      const merge = <T extends { id: string }>(current: T[], incoming: T[] | undefined): T[] => {
        if (!incoming || incoming.length === 0) return current
        const byId = new Map(incoming.map(row => [row.id, row]))
        return [...current.map(row => byId.get(row.id) ?? row), ...incoming.filter(row => !current.some(c => c.id === row.id))]
      }
      setState({
        tags: merge(state.tags, rows.tags),
        projects: merge(state.projects, rows.projects),
        sections: merge(state.sections, rows.sections),
        tasks: merge(state.tasks, rows.tasks),
        canvasNotes: merge(state.canvasNotes, rows.canvasNotes),
        taskComments: merge(state.taskComments, rows.taskComments),
        projectComments: merge(state.projectComments, rows.projectComments),
        taskReminders: merge(state.taskReminders, rows.taskReminders),
        timeEntries: merge(state.timeEntries, rows.timeEntries),
        goals: merge(state.goals, rows.goals),
        habits: merge(state.habits, rows.habits),
        habitLogs: merge(state.habitLogs, rows.habitLogs),
        rewardUsages: merge(state.rewardUsages, rows.rewardUsages),
        settings: { ...state.settings, ...Object.fromEntries((rows.settings ?? []).map(s => [s.key, s.value])) },
        preferences: { ...state.preferences, ...Object.fromEntries((rows.userPreferences ?? []).map(p => [p.key, p.value])) },
      })
    },
  }
}
